
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SlabDimensionsFields } from "./SlabDimensionsFields";
import { MaterialPrefix } from "@/types/slab";
import { PriceCategory } from "@/types/priceCategory";
import { calculatePricePerSqft, calculateSqft } from "@/lib/utils";
import { generateSlabCode } from "@/lib/codeGenerator";
import { toast } from "sonner";
import { SlabEditImage } from "@/components/slab/SlabEditImage";
import { ProductMainFields } from "./ProductMainFields";

export function ProductForm({ onSuccess, onCancel }: { 
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: '',
    price: 0,
    pricePerSqft: 0,
    sqft: 0,
    quantity: 1,
    location_id: '',
    price_group_id: '',
    imageUrl: '',
    dimensions: {
      length: 0,
      height: 0
    }
  });

  // Buscar prefixos de materiais
  const { data: materialPrefixes } = useQuery({
    queryKey: ['materialPrefixes'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cat_material_prefixes')
        .select('*')
        .order('material_type');
      return data as MaterialPrefix[] || [];
    }
  });

  // Buscar grupos de preço
  const { data: priceGroups } = useQuery({
    queryKey: ['priceGroups'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cat_price_groups')
        .select('*')
        .eq('active', true)
        .order('name');
      return data as PriceCategory[] || [];
    }
  });

  // Buscar localizações
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cat_locations')
        .select('*')
        .eq('active', true)
        .order('name');
      return data || [];
    }
  });

  // Atualizar código quando dados relevantes mudarem
  useEffect(() => {
    if (materialPrefixes && priceGroups && formData.type) {
      const newCode = generateSlabCode(
        formData.type,
        formData.price,
        formData.pricePerSqft,
        materialPrefixes,
        priceGroups
      );

      if (newCode !== formData.code) {
        setFormData(prev => ({
          ...prev,
          code: newCode
        }));
      }
    }
  }, [
    formData.type,
    formData.price,
    formData.pricePerSqft,
    materialPrefixes,
    priceGroups
  ]);

  // Atualizar preço por SQFT quando dimensões ou preço mudarem
  useEffect(() => {
    const calculatedSqft = calculateSqft(
      formData.dimensions.length,
      formData.dimensions.height
    );
    
    const calculatedPricePerSqft = calculatePricePerSqft(
      formData.price,
      calculatedSqft
    );

    setFormData(prev => ({
      ...prev,
      sqft: calculatedSqft,
      pricePerSqft: calculatedPricePerSqft
    }));
  }, [formData.dimensions, formData.price]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { error } = await supabase
        .from('cat_slabs')
        .insert([{
          code: formData.code,
          name: formData.name,
          type: formData.type,
          price: formData.price,
          price_per_sqft: formData.pricePerSqft,
          sqft: formData.sqft,
          quantity: formData.quantity,
          location_id: formData.location_id,
          price_group_id: formData.price_group_id,
          dimensions: formData.dimensions,
          image_url: formData.imageUrl
        }]);

      if (error) throw error;

      toast.success("Produto criado com sucesso!");
      onSuccess();
    } catch (error) {
      console.error('Erro ao criar produto:', error);
      toast.error("Erro ao criar produto");
    }
  };

  const handleFieldChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = (newImageUrl: string) => {
    setFormData(prev => ({
      ...prev,
      imageUrl: newImageUrl
    }));
  };

  const handleDimensionsChange = (dimension: 'length' | 'height', value: number) => {
    setFormData(prev => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [dimension]: value
      }
    }));
  };

  const handleQuantityChange = (value: number) => {
    setFormData(prev => ({
      ...prev,
      quantity: value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <SlabEditImage
        imageUrl={formData.imageUrl}
        name={formData.name}
        onImageChange={handleImageChange}
      />

      <ProductMainFields
        formData={formData}
        materialPrefixes={materialPrefixes}
        priceGroups={priceGroups}
        locations={locations}
        onFieldChange={handleFieldChange}
      />

      <SlabDimensionsFields
        formData={formData}
        onDimensionsChange={handleDimensionsChange}
        onQuantityChange={handleQuantityChange}
      />

      <div className="flex justify-end space-x-2">
        <Button variant="outline" type="button" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit">
          Salvar
        </Button>
      </div>
    </form>
  );
}
